import { useState } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import { cn } from '@antimatterhq/ui/lib/utils';

type JsonTextProps = {
  data: object;
  'data-testid'?: string;
};

type JsonNodeProps = JsonTextProps & {
  name?: string;
  truncate?: boolean;
  defaultExpanded?: boolean;
  depth?: number;
};

const getValueStyle = (value: unknown): { text: string; className: string } => {
  if (typeof value === 'boolean') {
    return {
      text: value.toString(),
      className: 'text-purple-600 dark:text-purple-400',
    };
  }
  if (typeof value === 'number') {
    return {
      text: value.toString(),
      className: 'text-blue-600 dark:text-blue-400',
    };
  }
  if (value === null) {
    return {
      text: 'null',
      className: 'text-gray-500 dark:text-gray-400',
    };
  }
  if (value === undefined) {
    return {
      text: 'undefined',
      className: 'text-gray-500 dark:text-gray-400',
    };
  }
  return {
    text: `"${value}"`,
    className: 'text-green-600 dark:text-green-400',
  };
};

const renderCollapsedValue = (value: unknown): JSX.Element => {
  const { text, className } = getValueStyle(value);
  return <span className={className}>{text}</span>;
};

const renderCollapsedObject = (obj: object): JSX.Element => {
  const entries = Object.entries(obj);
  const isArray = Array.isArray(obj);

  return (
    <span className="font-mono text-sm">
      <span className="text-gray-600 dark:text-gray-300">
        {isArray ? '[' : '{'}
      </span>
      {entries.map(([key, value], index) => (
        <span key={key}>
          {!isArray && (
            <>
              <span className="text-red-600 dark:text-red-400">
                &quot;{key}&quot;
              </span>
              <span className="text-gray-600 dark:text-gray-300">: </span>
            </>
          )}
          {typeof value === 'object' && value !== null ? (
            <span className="text-gray-600 dark:text-gray-300">
              {Array.isArray(value) ? '[...]' : '{...}'}
            </span>
          ) : (
            renderCollapsedValue(value)
          )}
          {index < entries.length - 1 && (
            <span className="text-gray-600 dark:text-gray-300">, </span>
          )}
        </span>
      ))}
      <span className="text-gray-600 dark:text-gray-300">
        {isArray ? ']' : '}'}
      </span>
    </span>
  );
};

const JsonNode = ({
  data,
  'data-testid': dataTestId,
  truncate,
  defaultExpanded = true,
  depth = 0,
}: JsonNodeProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const entries = Object.entries(data);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="flex w-full items-start font-mono text-sm">
      <Button
        iconOnly
        leftIcon={isExpanded ? 'lucide:chevron-down' : 'lucide:chevron-right'}
        variant="ghost"
        size="small"
        onClick={toggleExpand}
        className="mr-1 w-[14px] flex-shrink-0 p-0"
        data-testid={`${dataTestId}-toggle`}
      />

      {!isExpanded ? (
        <div
          className={cn(
            'font-mono text-sm',
            truncate && 'truncate',
            !truncate && 'text-wrap',
          )}
        >
          {renderCollapsedObject(data)}
        </div>
      ) : (
        <div className="flex-1 font-mono text-sm">
          <div className="text-gray-600 dark:text-gray-300">
            {Array.isArray(data) ? '[' : '{'}
          </div>

          {entries.map(([key, value], index) => {
            const isLast = index === entries.length - 1;
            return (
              <div key={key}>
                {typeof value === 'object' && value !== null ? (
                  <>
                    <div style={{ paddingLeft: `${12}px` }}>
                      <span className="text-red-600 dark:text-red-400">
                        &quot;{key}&quot;
                      </span>
                      <span className="text-gray-600 dark:text-gray-300">
                        :
                      </span>
                    </div>
                    <JsonNode
                      data={value}
                      data-testid={`${dataTestId}-${key}`}
                      depth={depth + 1}
                      defaultExpanded={true}
                    />
                    {!isLast && (
                      <div
                        style={{ paddingLeft: `${12}px` }}
                        className="text-gray-600 dark:text-gray-300"
                      >
                        ,
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ paddingLeft: `${12}px` }}>
                    <span className="text-red-600 dark:text-red-400">
                      &quot;{key}&quot;
                    </span>
                    <span className="text-gray-600 dark:text-gray-300">: </span>
                    <span className={getValueStyle(value).className}>
                      {getValueStyle(value).text}
                    </span>
                    {!isLast && (
                      <span className="text-gray-600 dark:text-gray-300">
                        ,
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <div className="text-gray-600 dark:text-gray-300">
            {Array.isArray(data) ? ']' : '}'}
          </div>
        </div>
      )}
    </div>
  );
};

export const JsonText = ({
  data,
  'data-testid': dataTestId,
}: JsonTextProps) => {
  return (
    <JsonNode
      data={data}
      data-testid={dataTestId}
      truncate
      defaultExpanded={false}
      depth={0}
    />
  );
};
